import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Building2, Plus, Search, Pencil, Trash2, Eye, Phone, Mail, 
  MapPin, FileText, DollarSign, Users, Filter, X, ArrowLeft
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

const VENDOR_TYPES = [
  { value: 'supplier', label: 'Supplier' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'service_provider', label: 'Service Provider' },
  { value: 'utility', label: 'Utility' },
  { value: 'insurance', label: 'Insurance Provider' },
  { value: 'landlord', label: 'Landlord' },
  { value: 'other', label: 'Other' }
];

const PAYMENT_TERMS = [
  { value: 'immediate', label: 'Due on Receipt' },
  { value: 'net15', label: 'Net 15' },
  { value: 'net30', label: 'Net 30' },
  { value: 'net45', label: 'Net 45' },
  { value: 'net60', label: 'Net 60' },
  { value: 'net90', label: 'Net 90' }
];

const PROVINCES = [
  'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'
];

export default function Vendors() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorTransactions, setVendorTransactions] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');

  // Form state
  const [vendorForm, setVendorForm] = useState({
    name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    province: 'MB',
    postal_code: '',
    country: 'Canada',
    tax_id: '',
    vendor_type: 'supplier',
    payment_terms: 'net30',
    default_expense_category: '',
    notes: '',
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, [searchQuery, typeFilter, activeFilter]);

  const loadData = async () => {
    setLoading(true);
    const token = getToken();
    try {
      // Load vendors with filters
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (typeFilter) params.append('vendor_type', typeFilter);
      if (activeFilter !== '') params.append('is_active', activeFilter);

      const [vendorsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/api/vendors?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/accounting/chart-of-accounts`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setVendors(vendorsRes.data);
      // Filter to expense accounts only
      setExpenseCategories(categoriesRes.data.filter(a => a.account_type === 'expense'));
    } catch (err) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setVendorForm({
      name: '',
      contact_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      province: 'MB',
      postal_code: '',
      country: 'Canada',
      tax_id: '',
      vendor_type: 'supplier',
      payment_terms: 'net30',
      default_expense_category: '',
      notes: '',
      is_active: true
    });
    setEditingVendor(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (vendor) => {
    setEditingVendor(vendor);
    setVendorForm({
      name: vendor.name,
      contact_name: vendor.contact_name || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      address: vendor.address || '',
      city: vendor.city || '',
      province: vendor.province || 'MB',
      postal_code: vendor.postal_code || '',
      country: vendor.country || 'Canada',
      tax_id: vendor.tax_id || '',
      vendor_type: vendor.vendor_type,
      payment_terms: vendor.payment_terms,
      default_expense_category: vendor.default_expense_category || '',
      notes: vendor.notes || '',
      is_active: vendor.is_active
    });
    setShowModal(true);
  };

  const saveVendor = async () => {
    if (!vendorForm.name.trim()) {
      toast.error('Vendor name is required');
      return;
    }

    const token = getToken();
    try {
      if (editingVendor) {
        await axios.put(`${API}/api/vendors/${editingVendor.id}`, vendorForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Vendor updated successfully');
      } else {
        await axios.post(`${API}/api/vendors`, vendorForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Vendor created successfully');
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save vendor');
    }
  };

  const deleteVendor = async (vendor) => {
    if (!confirm(`Are you sure you want to delete "${vendor.name}"?`)) return;

    const token = getToken();
    try {
      const response = await axios.delete(`${API}/api/vendors/${vendor.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      loadData();
    } catch (err) {
      toast.error('Failed to delete vendor');
    }
  };

  const viewVendorDetails = async (vendor) => {
    setSelectedVendor(vendor);
    const token = getToken();
    try {
      const response = await axios.get(`${API}/api/vendors/${vendor.id}/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendorTransactions(response.data);
      setShowDetailModal(true);
    } catch (err) {
      toast.error('Failed to load vendor details');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setTypeFilter('');
    setActiveFilter('');
  };

  // Stats
  const totalVendors = vendors.length;
  const activeVendors = vendors.filter(v => v.is_active).length;
  const totalSpent = vendors.reduce((sum, v) => sum + (v.total_spent || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Building2 className="w-7 h-7 text-indigo-600" />
                Vendor Management
              </h1>
              <p className="text-slate-500 text-sm">Manage suppliers, contractors, and service providers</p>
            </div>
          </div>
          {isAdmin && (
            <Button onClick={openCreateModal} data-testid="create-vendor-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Vendor
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-white/80 backdrop-blur">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Vendors</p>
                  <p className="text-2xl font-bold">{totalVendors}</p>
                </div>
                <Building2 className="w-10 h-10 text-indigo-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/80 backdrop-blur">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Active Vendors</p>
                  <p className="text-2xl font-bold text-emerald-600">{activeVendors}</p>
                </div>
                <Users className="w-10 h-10 text-emerald-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/80 backdrop-blur">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Spent (All Time)</p>
                  <p className="text-2xl font-bold text-amber-600">{formatCurrency(totalSpent)}</p>
                </div>
                <DollarSign className="w-10 h-10 text-amber-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-white/80 backdrop-blur">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, contact, email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="w-40">
                <Label className="text-xs">Type</Label>
                <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {VENDOR_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-32">
                <Label className="text-xs">Status</Label>
                <Select value={activeFilter || "all"} onValueChange={(v) => setActiveFilter(v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" /> Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Vendors Table */}
        <Card className="bg-white/80 backdrop-blur">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Payment Terms</TableHead>
                  <TableHead>Default Category</TableHead>
                  <TableHead className="text-right">Total Spent</TableHead>
                  <TableHead>Status</TableHead>
                  {isAdmin && <TableHead>Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      Loading vendors...
                    </TableCell>
                  </TableRow>
                ) : vendors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      No vendors found. {isAdmin && "Click 'Add Vendor' to create one."}
                    </TableCell>
                  </TableRow>
                ) : (
                  vendors.map(vendor => (
                    <TableRow key={vendor.id} className={!vendor.is_active ? 'opacity-50' : ''}>
                      <TableCell>
                        <div className="font-medium">{vendor.name}</div>
                        {vendor.tax_id && (
                          <div className="text-xs text-slate-500">Tax ID: {vendor.tax_id}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {vendor.contact_name && <div>{vendor.contact_name}</div>}
                          {vendor.email && (
                            <div className="flex items-center gap-1 text-slate-500">
                              <Mail className="w-3 h-3" /> {vendor.email}
                            </div>
                          )}
                          {vendor.phone && (
                            <div className="flex items-center gap-1 text-slate-500">
                              <Phone className="w-3 h-3" /> {vendor.phone}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {vendor.vendor_type.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {PAYMENT_TERMS.find(t => t.value === vendor.payment_terms)?.label || vendor.payment_terms}
                        </span>
                      </TableCell>
                      <TableCell>
                        {vendor.default_expense_category_name ? (
                          <Badge variant="outline" className="text-xs">
                            {vendor.default_expense_category_name}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(vendor.total_spent || 0)}
                        {vendor.invoice_count > 0 && (
                          <div className="text-xs text-slate-500">{vendor.invoice_count} txns</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={vendor.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}>
                          {vendor.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      {isAdmin && (
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => viewVendorDetails(vendor)} title="View Details">
                              <Eye className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openEditModal(vendor)} title="Edit">
                              <Pencil className="w-4 h-4 text-slate-500" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteVendor(vendor)} title="Delete">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) { setShowModal(false); resetForm(); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" />
              {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
            </DialogTitle>
            <DialogDescription>
              {editingVendor ? 'Update vendor information' : 'Enter vendor details below'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vendor Name *</Label>
                <Input
                  value={vendorForm.name}
                  onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })}
                  placeholder="Company or individual name"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Name</Label>
                <Input
                  value={vendorForm.contact_name}
                  onChange={(e) => setVendorForm({ ...vendorForm, contact_name: e.target.value })}
                  placeholder="Primary contact person"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={vendorForm.email}
                  onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })}
                  placeholder="vendor@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={vendorForm.phone}
                  onChange={(e) => setVendorForm({ ...vendorForm, phone: e.target.value })}
                  placeholder="(204) 555-0123"
                />
              </div>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={vendorForm.address}
                onChange={(e) => setVendorForm({ ...vendorForm, address: e.target.value })}
                placeholder="Street address"
              />
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>City</Label>
                <Input
                  value={vendorForm.city}
                  onChange={(e) => setVendorForm({ ...vendorForm, city: e.target.value })}
                  placeholder="Winnipeg"
                />
              </div>
              <div className="space-y-2">
                <Label>Province</Label>
                <Select value={vendorForm.province} onValueChange={(v) => setVendorForm({ ...vendorForm, province: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROVINCES.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Postal Code</Label>
                <Input
                  value={vendorForm.postal_code}
                  onChange={(e) => setVendorForm({ ...vendorForm, postal_code: e.target.value })}
                  placeholder="R3E 0E3"
                />
              </div>
            </div>

            {/* Business Info */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Vendor Type</Label>
                <Select value={vendorForm.vendor_type} onValueChange={(v) => setVendorForm({ ...vendorForm, vendor_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VENDOR_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Payment Terms</Label>
                <Select value={vendorForm.payment_terms} onValueChange={(v) => setVendorForm({ ...vendorForm, payment_terms: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TERMS.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tax ID / Business #</Label>
                <Input
                  value={vendorForm.tax_id}
                  onChange={(e) => setVendorForm({ ...vendorForm, tax_id: e.target.value })}
                  placeholder="For T4A/1099"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Default Expense Category</Label>
              <Select 
                value={vendorForm.default_expense_category || "none"} 
                onValueChange={(v) => setVendorForm({ ...vendorForm, default_expense_category: v === "none" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No default category</SelectItem>
                  {expenseCategories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.account_number} - {cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={vendorForm.notes}
                onChange={(e) => setVendorForm({ ...vendorForm, notes: e.target.value })}
                placeholder="Additional notes about this vendor..."
                rows={3}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={vendorForm.is_active}
                onChange={(e) => setVendorForm({ ...vendorForm, is_active: e.target.checked })}
                className="rounded border-slate-300"
              />
              <Label htmlFor="is_active" className="cursor-pointer">Active vendor</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>Cancel</Button>
            <Button onClick={saveVendor}>
              {editingVendor ? 'Update Vendor' : 'Create Vendor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vendor Details Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" />
              Vendor Details
            </DialogTitle>
            {selectedVendor && (
              <DialogDescription>
                {selectedVendor.name}
              </DialogDescription>
            )}
          </DialogHeader>
          {vendorTransactions && (
            <div className="space-y-6 py-4">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="bg-slate-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-slate-500">Total Expenses</p>
                    <p className="text-lg font-bold">{formatCurrency(vendorTransactions.summary.total_expenses)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-slate-500">Expense Count</p>
                    <p className="text-lg font-bold">{vendorTransactions.summary.expense_count}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-slate-500">Total Transactions</p>
                    <p className="text-lg font-bold">{formatCurrency(vendorTransactions.summary.total_transactions)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-50">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-slate-500">Transaction Count</p>
                    <p className="text-lg font-bold">{vendorTransactions.summary.transaction_count}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Expenses */}
              {vendorTransactions.expenses.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Recent Expenses</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Agent</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vendorTransactions.expenses.slice(0, 10).map(exp => (
                        <TableRow key={exp.id}>
                          <TableCell className="font-mono text-sm">{exp.expense_date}</TableCell>
                          <TableCell>{exp.description}</TableCell>
                          <TableCell>{exp.agent_name}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(exp.total_amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Recent Transactions */}
              {vendorTransactions.transactions.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Recent Transactions</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Account</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vendorTransactions.transactions.slice(0, 10).map(trans => (
                        <TableRow key={trans.id}>
                          <TableCell className="font-mono text-sm">{trans.date}</TableCell>
                          <TableCell>{trans.description}</TableCell>
                          <TableCell>{trans.account_name}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(trans.debit)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
